(ns nosco.richtext.file-uploads
  (:require
   ["react" :as React]
   ["nosco-objectid/nosco-objectid" :as ObjectId]
   [ajax.core :as ajax]
   [hx.react :as hx :refer [defnc]]
   [medley.core :as medley]
   [nosco.i18n.translate :as t]
   [nosco.ui.dropzone :as dropzone]
   [nosco.ui.progress :as progress]
   [nosco.ui.text :as text]
   [nosco.hooks :as nosco-hooks]
   [nosco.icons2 :as icons2]))

(defmulti ^:private file-upload-state-reducer (fn [_state action] (:type action)))

(defmethod file-upload-state-reducer ::reset
  [_ _]
  nil)

(defn- assoc-upload [state id upload]
  ;; Use a sorted map so that uploads have a stable order
  (update state :uploads (fnil assoc (sorted-map)) id upload))

(defmethod file-upload-state-reducer ::too-large-file-dropped
  [state {:keys [id filesize filename]}]
  (assoc-upload state id {:filesize filesize
                          :filename filename
                          :error ::file-too-large}))

(defmethod file-upload-state-reducer ::upload-started
  [state {:keys [id filesize filename file request]}]
  (update state :uploads
          ;; Use a sorted map so that uploads have a stable order
          (fnil assoc (sorted-map))
          id
          {:filesize filesize
           :filename filename
           :file file
           :request request
           :progress-max 100
           :progress-value 0}))

(defmethod file-upload-state-reducer ::upload-progress-changed
  [state {:keys [id progress-max progress-value]}]
  (medley/update-existing-in state [:uploads id]
                             assoc
                             :progress-max progress-max
                             :progress-value progress-value))

(defmethod file-upload-state-reducer ::upload-completed
  [state {:keys [id href file-id]}]
  (medley/update-existing-in state [:uploads id]
                             assoc
                             :href href
                             :file-id file-id
                             :uploaded? true))

(defmethod file-upload-state-reducer ::upload-failed
  [state {:keys [id]}]
  (medley/update-existing-in state [:uploads id] assoc :error true))

(defmethod file-upload-state-reducer ::upload-cancelled
  [state {:keys [id]}]
  (update state :uploads dissoc id))

(defn- cancel-all-requests! [state]
  (doseq [[_ {:keys [request]}] (:uploads state)]
    (when request
      (ajax/abort request))))

(defn use-file-upload-reducer []
  ;; Use apply because multimethods aren't JS functions
  (let [[state dispatch] (React/useReducer #(apply file-upload-state-reducer %&) {})]
    (nosco-hooks/use-unmount [state] (fn [state] (cancel-all-requests! state)))
    [state
     dispatch
     (fn reset []
       (cancel-all-requests! state)
       (dispatch {:type ::reset}))]))

(defn- upload-completed? [upload]
  (and (= (:progress-max upload) (:progress-value upload))
       (:file-id upload)))

(defn uploads-in-progress? [state]
  (some (complement upload-completed?) (-> state :uploads vals)))

(defn- upload-file [dispatch id ^js file]
  (ajax/POST
    "/files"
    {:body (doto (js/FormData.)
             (.append "file" file))
     :response-format (ajax/json-response-format {:keywords? true})
     :headers {:x-request-origin "prosemirror"}
     :progress-handler
     (fn [^js event]
       ;; N.B. The length of the request is different to the filesize, because it
       ;; also includes form data boundaries
       (dispatch {:type ::upload-progress-changed
                  :id id
                  :progress-value (.-loaded event)
                  :progress-max (.-total event)}))
     :handler
     (fn [response]
       (let [[file-id file]  (first response)
             filelink (:filelink file)]
         (dispatch {:type ::upload-completed
                    :id id
                    :file-id file-id
                    :href filelink})))
     :error-handler
     (fn []
       (dispatch {:type ::upload-failed
                  :id id}))}))

(def max-file-size-bytes
  (* 50   ; megabytes
     1000 ; kilobytes
     1000 ; bytes
     ))

(defn- handle-file-drop [dispatch ^js file]
  (let [id (str (ObjectId.))] ;; unique, but sortable by date
    (if (> (.-size file) max-file-size-bytes)
      (dispatch {:type ::too-large-file-dropped
                 :id id
                 :filesize (.-size file)
                 :filename (.-name file)})
      (dispatch {:type ::upload-started
                 :id id
                 :filesize (.-size file)
                 :filename (.-name file)
                 :file file
                 :request (upload-file dispatch id file)}))))

(defn- handle-retry [dispatch id upload]
  (upload-file dispatch id (:file upload)))

(defn- handle-cancel [dispatch id upload]
  (when (:request upload)
    (ajax/abort (:request upload)))
  (dispatch {:type ::upload-cancelled
             :id id}))

(defnc FileUploadProgressItem [{:keys [upload on-retry] :as props}]
  [progress/UploadProgressItem
   (merge (dissoc props :upload)
          {:filename (:filename upload)
           :uploaded? (:uploaded? upload)
           :filesize (:filesize upload)
           :progress-max (:progress-max upload)
           :progress-value (:progress-value upload)
           :thumb-icon icons2/File-large
           :error (case (:error upload)
                    ::file-too-large (t/t "File too large")
                    (some? (:error upload)))
           :on-retry (when (= (:error upload) true) on-retry)})])

(defnc FileUpload [{:keys [state dispatch]}]
  [:div
   [text/Text {:el :p :variant text/Body-Md-Regular}
    (t/t "You can upload PDF, ZIP or other files (max. file size: 50 MB)")]
   [dropzone/Dropzone
    {:max-files 10
     :width "100%"
     :on-drop
     (fn [files]
       (doseq [file files]
         (handle-file-drop dispatch file)))}]
   [:div {:class ["mt-spacing-32"]}
    [progress/ProgressList
     (for [[id upload] (:uploads state)]
       [FileUploadProgressItem
        {:upload upload
         :on-retry
         (fn []
           (handle-retry dispatch id upload))
         :on-cancel
         (fn []
           (handle-cancel dispatch id upload))}])]]])
